import {
  OrderManagementPermissions,
  ReturnsAndClaimsPermissions,
} from "@redotech/account-common/permissions";
import { useRequiredContext } from "@redotech/react-util/context";
import PackageIcon from "@redotech/redo-icons/package.svg";
import PackageFilledIcon from "@redotech/redo-icons/package_filled.svg";
import { useHasPermission } from "@redotech/redo-merchant-app-common/auth/permissions-context";
import { MerchantRoutesContext } from "@redotech/redo-merchant-app-common/merchant-routes";
import { LinkPrimaryTab } from "@redotech/redo-web/arbiter-components/navigation/primary/redo-primary-nav";
import { PageNavigationEntry } from "@redotech/redo-web/arbiter-components/navigation/redo-side-navigation";
import { SecondaryNavigationData } from "@redotech/redo-web/arbiter-components/navigation/secondary/redo-secondary-nav";
import { useNavTreeNode } from "@redotech/redo-web/arbiter-components/navigation/secondary/redo-secondary-nav-item";
import { memo } from "react";
import { OrdersReviewCountContext } from "../../../../services/orders/orders-review-count-service";
import { AnalyticsItem } from "./analytics";
import { ClaimsItem } from "./claims";
import { DashboardItem } from "./dashboard";
import { OrderEditsItem } from "./order-edits";
import { OrdersItem } from "./orders";
import { OutboundSection } from "./outbound";
import { RecycleItem } from "./recycle";
import { ReturnsItem } from "./returns";
import { WarehouseItem } from "./warehouse";
import { WarrantiesItem } from "./warranties";

export function useOrdersNavigation(): PageNavigationEntry | null {
  const routes = useRequiredContext(MerchantRoutesContext);

  const pathRoot = routes.shippingBaseUrl;

  const { claimsNeedingReview, returnsNeedingReview, warrantiesNeedingReview } =
    useRequiredContext(OrdersReviewCountContext);

  const canViewOrderManagementSection = useHasPermission(
    ReturnsAndClaimsPermissions.MANAGE_WARRANTY,
    ReturnsAndClaimsPermissions.MANAGE_RECYCLING_REQUESTS,
    ReturnsAndClaimsPermissions.VIEW_RETURN,
    OrderManagementPermissions.VIEW_ORDER,
    OrderManagementPermissions.SCAN_AND_VERIFY,
    OrderManagementPermissions.MANAGE_SHIPMENTS,
    ReturnsAndClaimsPermissions.MANAGE_PACKAGE_PROTECTION_CLAIM,
  );

  if (!canViewOrderManagementSection) {
    return null;
  }

  const hasNotification =
    !!claimsNeedingReview ||
    !!returnsNeedingReview ||
    !!warrantiesNeedingReview;

  const primaryTab: LinkPrimaryTab = {
    id: "orders",
    hasNotification,
    UnfilledIcon: PackageIcon,
    FilledIcon: PackageFilledIcon,
    type: "link",
    pathRoot,
    href: routes.shippingDefaultRoute,
    friendlyName: "Operations",
  };

  const secondaryNavigationData: SecondaryNavigationData = {
    id: "orders",
    name: "Operations",
    NavContent: OrdersSecondaryNavigation,
  };

  return { primaryTab, secondaryNavigationData, isActiveProduct: true };
}

const OrdersSecondaryNavigation = memo(function OrdersSecondaryNavigation() {
  const { id: nodeId, TreeContext } = useNavTreeNode({
    friendlyName: "Operations",
  });

  return (
    <TreeContext.Provider value={nodeId}>
      {/* Dashboard is the first item and default landing page for Operations */}
      <DashboardItem />
      {/* Remaining items are alphabetized */}
      <AnalyticsItem />
      <ClaimsItem />
      <OrderEditsItem />
      <OrdersItem />
      <OutboundSection />
      <RecycleItem />
      <ReturnsItem />
      <WarehouseItem />
      <WarrantiesItem />
    </TreeContext.Provider>
  );
});
